// NodeTags.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './NodeTags.css';

// Components
import Button from 'components/button/Button';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalForm from 'components/modal/form/ModalForm';

const TAG_SPLIT_REGEX = /[\n,]+/;

const normalizeTagKey = (tag) => String(tag || '').trim().toLowerCase();

const parseTags = (value) => {
    return value
        .split(TAG_SPLIT_REGEX)
        .map((token) => token.trim())
        .filter(Boolean);
};

const appendUniqueTags = (currentTags, nextTags) => {
    const seenTags = new Set(currentTags.map(normalizeTagKey));
    const additions = nextTags.filter((tag) => {
        const normalizedTag = normalizeTagKey(tag);

        if (!normalizedTag || seenTags.has(normalizedTag)) {
            return false;
        }

        seenTags.add(normalizedTag);
        return true;
    });

    return additions.length ? [...currentTags, ...additions] : currentTags;
};

/**
 * NodeTags
 *
 * Renders a chip-style tag editor for a node's freeform tags.
 *
 * @param {string[]} tags - Current tags array.
 * @param {{ value: string, count: number }[]} suggestions - Suggested tags for the current node type.
 * @param {boolean} suggestionsLoading - Whether suggestions are still loading.
 * @param {Function} onSave - Callback when tags are saved.
 * @param {Function} onCancel - Callback when editing is canceled.
 *
 * @returns {ReactNode} The rendered component.
 */
const NodeTags = ({ tags = [], suggestions = [], suggestionsLoading = false, onSave, onCancel }) => {

    // ----- REFS ----- //

    const inputRef = useRef(null);

    // ----- CONTEXT ----- //

    const { theme } = useTheme();

    // ----- STATE ----- //

    const [currentTags, setCurrentTags] = useState([...tags]);
    const [inputValue, setInputValue] = useState('');
    const [hoveredRemoveTag, setHoveredRemoveTag] = useState(null);

    // ----- MEMOS ----- //

    const visibleSuggestions = useMemo(() => {
        const normalizedSelectedTags = new Set(currentTags.map(normalizeTagKey));
        const normalizedInput = normalizeTagKey(inputValue);

        return suggestions
            .filter((suggestion) => {
                const suggestionValue = String(suggestion?.value || '').trim();
                const normalizedSuggestion = normalizeTagKey(suggestionValue);

                if (!normalizedSuggestion || normalizedSelectedTags.has(normalizedSuggestion)) {
                    return false;
                }

                if (!normalizedInput) {
                    return true;
                }

                return normalizedSuggestion.includes(normalizedInput);
            })
            .sort((a, b) => {
                const aValue = String(a?.value || '').trim();
                const bValue = String(b?.value || '').trim();
                const aNormalized = normalizeTagKey(aValue);
                const bNormalized = normalizeTagKey(bValue);
                const aStarts = normalizedInput ? aNormalized.startsWith(normalizedInput) : false;
                const bStarts = normalizedInput ? bNormalized.startsWith(normalizedInput) : false;

                if (aStarts !== bStarts) {
                    return aStarts ? -1 : 1;
                }

                if ((b?.count || 0) !== (a?.count || 0)) {
                    return (b?.count || 0) - (a?.count || 0);
                }

                return aValue.localeCompare(bValue, undefined, { sensitivity: 'base' });
            })
            .slice(0, normalizedInput ? 8 : 10);
    }, [currentTags, inputValue, suggestions]);

    // ----- EFFECTS ----- //

    useEffect(() => {
        setCurrentTags([...tags]);
        setInputValue('');
        setHoveredRemoveTag(null);
    }, [tags]);

    // ----- MEMOS ----- //

    const focusInput = useCallback(() => {
        inputRef.current?.focus();
    }, []);

    // ----- HANDLERS ----- //

    /**
 * Handles add tags.
 *
 * @param {*} value
 */

    const handleAddTags = useCallback((value) => {
        const parsedTags = parseTags(value);

        if (!parsedTags.length) {
            return false;
        }

        setCurrentTags((previousTags) => appendUniqueTags(previousTags, parsedTags));
        setInputValue('');

        return true;
    }, []);

    /**
 * Handles remove tag.
 *
 * @param {*} tagToRemove
 */
    const handleRemoveTag = useCallback((tagToRemove) => {
        setCurrentTags((previousTags) => previousTags.filter((tag) => tag !== tagToRemove));
        focusInput();
    }, [focusInput]);

    /**
 * Handles input key down.
 *
 * @param {*} event
 */
    const handleInputKeyDown = useCallback((event) => {
        if ((event.key === 'Enter' || event.key === 'Tab' || event.key === ',') && inputValue.trim()) {
            event.preventDefault();
            handleAddTags(inputValue);
            return;
        }

        if (event.key === 'Backspace' && !inputValue && currentTags.length) {
            event.preventDefault();
            setCurrentTags((previousTags) => previousTags.slice(0, -1));
        }
    }, [currentTags.length, handleAddTags, inputValue]);

    /**
 * Handles input paste.
 *
 * @param {*} event
 */
    const handleInputPaste = useCallback((event) => {
        const pastedValue = event.clipboardData?.getData('text') || '';

        if (!pastedValue.match(TAG_SPLIT_REGEX)) {
            return;
        }

        event.preventDefault();
        handleAddTags(pastedValue);
    }, [handleAddTags]);

    /**
 * Handles editor click.
 *
 * @param {*} event
 */
    const handleEditorClick = useCallback((event) => {
        const removeButton = event.target.closest('.node-tags-remove');

        if (removeButton) {
            return;
        }

        focusInput();
    }, [focusInput]);

    /**
     * Handles save.
     */
    const handleSave = useCallback(() => {
        const nextTags = inputValue.trim()
            ? appendUniqueTags(currentTags, parseTags(inputValue))
            : currentTags;

        onSave(nextTags);
    }, [currentTags, inputValue, onSave]);

    /**
     * Handles suggestion click.
     *
     * @param {*} tag
     */
    const handleSuggestionClick = useCallback((tag) => {
        setCurrentTags((previousTags) => appendUniqueTags(previousTags, [tag]));
        setInputValue('');
        focusInput();
    }, [focusInput]);

    // ----- RENDER ----- //

    return (
        <>
            <ModalForm>
                
                <div className="node-tags-container">

                    {/* EDITOR */}
                    <div
                        className="node-tags-editor"
                        role="presentation"
                        onClick={handleEditorClick}
                    >
                        {currentTags.map((tag) => (
                            <span
                                className="node-tags-tag"
                                key={tag}
                            >

                                {/* TAG LABEL */}
                                <span className="node-tags-tag-label">{tag}</span>

                                {/* REMOVE */}
                                <button
                                    className="node-tags-remove"
                                    style={{
                                        backgroundColor: hoveredRemoveTag === tag ? theme.backgroundHover : theme.backgroundMuted,
                                        color: hoveredRemoveTag === tag ? theme.foreground : theme.foregroundMuted,
                                    }}
                                    type="button"
                                    aria-label={`Remove ${tag} tag`}
                                    onClick={() => handleRemoveTag(tag)}
                                    onMouseEnter={() => setHoveredRemoveTag(tag)}
                                    onMouseLeave={() => setHoveredRemoveTag(null)}
                                >
                                    ×
                                </button>
                            </span>
                        ))}

                        {/* INPUT */}
                        <input
                            className="node-tags-input"
                            ref={inputRef}
                            autoFocus
                            maxLength={50}
                            placeholder={currentTags.length ? 'Add another tag' : 'Add tags'}
                            type="text"
                            value={inputValue}
                            onChange={(event) => setInputValue(event.target.value)}
                            onKeyDown={handleInputKeyDown}
                            onPaste={handleInputPaste}
                        />
                    </div>

                    {/* HELPER */}
                    <div
                        className="node-tags-helper"
                    >
                        Press Enter, comma, or Tab to add tags. Paste comma-separated tags to add several at once.
                    </div>

                    {(suggestionsLoading || visibleSuggestions.length > 0) && (
                        <div className="node-tags-suggestions-container">

                            {/* SUGGESTIONS LABEL */}
                            <div
                                className="node-tags-suggestions-label"
                            >
                                Suggestions from this type
                            </div>

                            {suggestionsLoading ? (
                                <div
                                    className="node-tags-suggestions-empty"
                                >
                                    Loading suggestions...
                                </div>
                            ) : (

                                <div className="node-tags-suggestions-list">
                                    {visibleSuggestions.map((suggestion) => (
                                        <button
                                            className="node-tags-suggestion"
                                            key={suggestion.value}
                                            type="button"
                                            onClick={() => handleSuggestionClick(suggestion.value)}
                                        >

                                            {/* SUGGESTION LABEL */}
                                            <span className="node-tags-suggestion-label">{suggestion.value}</span>

                                            {/* SUGGESTION COUNT */}
                                            <span
                                                className="node-tags-suggestion-count"
                                            >
                                                {suggestion.count}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                </div>
            </ModalForm>

            <ModalButtons>
                {onCancel && (
                    <Button
                        backgroundColor={theme.backgroundMuted}
                        borderColor={theme.backgroundHover}
                        label="Cancel"
                        onClick={onCancel}
                    />
                )}
                <Button
                    label="Save"
                    onClick={handleSave}
                />
            </ModalButtons>
        </>
    );
};

// Dev Tools
NodeTags.displayName = 'NodeTags';

export default NodeTags;
